import React, { useState, useEffect } from 'react';
import { Bell, X, AlertCircle, Info, CheckCircle, Plus } from 'lucide-react';
import { doc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { portalDb, portalAuth } from '../utils/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { toast } from 'react-hot-toast';

const NotificationCenter = () => {
  const [user] = useAuthState(portalAuth);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch notifications when user logs in
  useEffect(() => {
    if (!user) return;
    
    // Using onSnapshot for real-time updates to notifications
    const userRef = doc(portalDb, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const userData = docSnapshot.data();
        const userNotifications = userData.notifications || [];
        
        // Sort notifications by timestamp (newest first)
        userNotifications.sort((a, b) => {
          return new Date(b.timestamp) - new Date(a.timestamp);
        });
        
        setNotifications(userNotifications);
        
        // Count unread notifications
        const unread = userNotifications.filter(notif => !notif.read).length;
        setUnreadCount(unread);
      }
    }, (error) => {
      console.error('Error in notification listener:', error);
    });
    
    // Clean up listener on unmount
    return () => unsubscribe();
  }, [user]);

  const toggleNotificationCenter = () => {
    setIsOpen(!isOpen);
  };

  const markAllAsRead = async () => {
    if (!user || notifications.length === 0) return;
    
    try {
      const updatedNotifications = notifications.map(notif => ({
        ...notif,
        read: true
      }));
      
      await updateDoc(doc(portalDb, 'users', user.uid), {
        notifications: updatedNotifications
      });
      
      setNotifications(updatedNotifications);
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      toast.error('Failed to update notifications');
    }
  };

  const markAsRead = async (notificationId) => {
    if (!user) return;
    
    try {
      const updatedNotifications = notifications.map(notif => {
        if (notif.id === notificationId) {
          return { ...notif, read: true };
        }
        return notif;
      });
      
      await updateDoc(doc(portalDb, 'users', user.uid), {
        notifications: updatedNotifications
      });
      
      setNotifications(updatedNotifications);
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    if (!user) return;
    
    try {
      const updatedNotifications = notifications.filter(notif => notif.id !== notificationId);
      
      await updateDoc(doc(portalDb, 'users', user.uid), {
        notifications: updatedNotifications
      });
      
      setNotifications(updatedNotifications);
      
      // Recalculate unread count
      const unread = updatedNotifications.filter(notif => !notif.read).length;
      setUnreadCount(unread);
      
      toast.success('Notification removed');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to remove notification');
    }
  };

  const clearAllNotifications = async () => {
    if (!user || notifications.length === 0) return;
    
    try {
      await updateDoc(doc(portalDb, 'users', user.uid), {
        notifications: []
      });
      
      setNotifications([]);
      setUnreadCount(0);
      toast.success('All notifications cleared');
    } catch (error) {
      console.error('Error clearing notifications:', error);
      toast.error('Failed to clear notifications');
    }
  };

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'report_removed':
      case 'report_deleted':
        return <AlertCircle className="h-5 w-5 text-orange-500" />;
      case 'report_restored':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'report_added':
        return <Plus className="h-5 w-5 text-blue-500" />;
      default:
        return <Bell className="h-5 w-5 text-blue-500" />;
    }
  };

  // Format timestamp to readable date
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      // If today, show time only
      const today = new Date();
      if (date.toDateString() === today.toDateString()) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      
      // If this year, show month and day
      if (date.getFullYear() === today.getFullYear()) {
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      }
      
      // Otherwise show full date
      return date.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Unknown date';
    }
  };

  return (
    <div className="relative inline-block">
      {/* Bell icon with notification count */}
      <button 
        onClick={toggleNotificationCenter}
        className="relative p-2 rounded-full text-gray-700 hover:bg-gray-100 focus:outline-none"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full min-w-[18px] h-[18px]">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification panel */}
      {isOpen && (
        <>
          {/* Add an overlay to block all elements underneath */}
          <div className="fixed inset-0 bg-transparent z-50" onClick={toggleNotificationCenter}></div>
          
          {/* Notification panel */}
          <div className="fixed top-[60px] right-4 w-80 bg-white rounded-md shadow-xl border border-gray-200 overflow-hidden z-50 max-h-[80vh] flex flex-col" style={{ backdropFilter: 'none', WebkitBackdropFilter: 'none' }}>
            <div className="px-4 py-3 bg-gray-50 border-b flex justify-between items-center sticky top-0 z-50">
              <h3 className="text-sm font-medium text-gray-700">Notifications</h3>
              <div className="flex space-x-2">
                {notifications.length > 0 && (
                  <>
                    <button 
                      onClick={markAllAsRead} 
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Mark all as read
                    </button>
                    <span className="text-gray-300">|</span>
                    <button 
                      onClick={clearAllNotifications} 
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Clear all
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="overflow-y-auto flex-1 bg-white relative">
              {notifications.length === 0 ? (
                <div className="py-8 px-4 text-center text-gray-500 bg-white">
                  <p>No notifications</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-100 bg-white">
                  {notifications.map((notification) => (
                    <li 
                      key={notification.id} 
                      className={`px-4 py-3 hover:bg-gray-50 ${!notification.read ? 'bg-blue-50' : 'bg-white'} relative`}
                    >
                      <div 
                        className="flex items-start cursor-pointer"
                        onClick={() => !notification.read && markAsRead(notification.id)}
                      >
                        <div className="flex-shrink-0 mt-1">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="ml-3 flex-1 pr-8"> {/* Increased right padding to make room for X button */}
                          <div className="flex justify-between items-start">
                            <p className="text-sm font-medium text-gray-900">
                              {notification.title}
                            </p>
                            <span className="text-xs text-gray-500 ml-2">
                              {formatTimestamp(notification.timestamp)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {notification.message}
                          </p>
                        </div>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification.id);
                          }}
                          className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 p-1 bg-transparent z-50"
                          aria-label="Delete notification"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationCenter; 